import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, Inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import {
	MAT_DIALOG_DATA,
	MatDialog,
	MatDialogRef,
} from '@angular/material/dialog';
import { DocumentDialogComponent } from '@components/document-dialog';
import { createId } from '@core/util/file';
import { Document } from '@models/matter';
import { DocumentMargins, DocumentLocation, PageSize } from '@models/documents';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterDocumentsRepoService } from '@repos/matter-documents/matter-documents-repo.service';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import { BusinessState } from '@state/business';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { format } from 'date-fns';
import { intersection } from 'lodash';
import { DocumentService } from 'src/app/shared/services/document/document.service';
import { ICON } from '@core/util/icon';
import { SelectedMatterDocumentsDispatch } from '@state/global/documents/selected-matter-documents.dispatch';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

export interface MergeDocumentDialogClose {
	documentId: string;
	title: string;
}

export interface MergeDocumentDialogData {
	documents: Document[];
	location: DocumentLocation;
}

@Component({
	selector: 'app-merge-document-dialog',
	templateUrl: './merge-document-dialog.component.html',
	standalone: false,
})
export class MergeDocumentDialogComponent {
	public documents: Document[];
	public error: string;
	public ICON = ICON;
	@SelectSnapshot(AuthState.getLoggedInUser) public loggedInUser: AuthUser;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(BusinessState.getDefaultPageMargins)
	defaultMargins: DocumentMargins;
	public title: string;
	public working: boolean;
	drawerService = inject(DrawerService);

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: { documents: Document[]; location: DocumentLocation },
		private dialog: MatDialog,
		private dialogRef: MatDialogRef<MergeDocumentDialogComponent>,
		private matterDocumentsRepoService: MatterDocumentsRepoService,
		private documentService: DocumentService,
		private selectedMatterDocumentsDispatch: SelectedMatterDocumentsDispatch,
	) {
		this.documents = this.data.documents;
		this.title = `Merged_${this.documents[0]?.title}`;
	}

	createMergedDocument(form: NgForm): void {
		const { title, working } = this;
		if (!(form.valid && title) || working) {
			return;
		}
		const documentId = createId();
		// Adds defaultMargins as fallback for old businesses documents that don't have margins set
		const documentMargins = this.documents.map(
			(doc) => doc.margins || this.defaultMargins,
		);
		const margins: DocumentMargins = {
			bottom: Math.min(...documentMargins.map((margin) => margin.bottom)),
			left: Math.min(...documentMargins.map((margin) => margin.left)),
			right: Math.min(...documentMargins.map((margin) => margin.right)),
			top: Math.min(...documentMargins.map((margin) => margin.top)),
			unit: documentMargins[0].unit,
		};
		this.error = '';
		this.working = true;

		this.matterDocumentsRepoService
			.Create(
				{
					...this.documentService.getDefaultDocument(),
					margins,
					author: this.loggedInUser.uid,
					collection: this.documents.map((document) => document.id),
					date: format(Date.now(), 'yyyy-MM-dd'),
					id: documentId,
					matterType: intersection(
						...this.documents.map((document) => document.matterType),
					),
					pageSize: this.documents.filter(
						(document) => document.pageSize === PageSize.LEGAL,
					).length
						? PageSize.LEGAL
						: PageSize.LETTER,
					title,
				},
				{ matterId: this.matterId },
			)
			.then(() => {
				const closeData: MergeDocumentDialogClose = {
					documentId,
					title,
				};

				this.drawerService.openDrawer(DocumentDialogComponent, {
					title,
					noStyle: true,
					data: {
						documentId,
						showStateSelector: false,
						title,
					},
				});
				this.dialogRef.close(closeData);
			})
			.catch(() => (this.error = 'Please try again.'));
	}

	cancelModal(): void {
		this.dialogRef.close(false);
	}

	download() {
		this.selectedMatterDocumentsDispatch.DownloadSelectedDocuments(this.title);
	}

	documentIndex(index: number, document: Document): string {
		return document.id;
	}

	onDrop(event: CdkDragDrop<string[]>): void {
		moveItemInArray(this.documents, event.previousIndex, event.currentIndex);
		// updating the order in the state
		const order = this.documents.map((x) => x.id);
		this.selectedMatterDocumentsDispatch.SetMergeOrder(order);
	}
}
